import { useQuery } from '@apollo/client';

import type { SeoLinkBoxInput } from '@xing-com/crate-common-graphql-types';
import type { SerpsListItem } from '@xing-com/crate-jobs-domain-serps-constants';
import { SeoLinkBoxesDocument } from '@xing-com/crate-jobs-domain-serps-graphql';

import { useSerpsMatch } from './use-serps-match';

type UseSeoLinkBoxes = {
  data?: {
    kind: string;
    entries: SerpsListItem[];
  }[];
  loading: boolean;
  error?: Error | undefined;
};

const useGetSerpType = (): string => {
  const serpMatch = useSerpsMatch();

  if (serpMatch?.category === 'role' && serpMatch?.subcategory === 'city') {
    return 'local-jobRole';
  }

  if (serpMatch?.category === 'role') {
    return 'jobRole';
  }

  if (serpMatch?.category === 'skill' && serpMatch?.subcategory === 'city') {
    return 'local-skill';
  }

  if (serpMatch?.category === 'skill') {
    return 'skill';
  }

  if (serpMatch?.category === 'city') {
    return 'location';
  }

  return '';
};

export const useSeoLinkBoxes = (): UseSeoLinkBoxes => {
  const serpType = useGetSerpType();
  const serpMatch = useSerpsMatch();
  const variables: SeoLinkBoxInput = {
    ...((serpType === 'local-skill' || serpType === 'skill') && {
      skill: serpMatch?.categoryValue,
    }),
    ...((serpType === 'local-jobRole' || serpType === 'jobRole') && {
      jobRole: serpMatch?.categoryValue,
    }),
    serpType: serpType,
  };

  if (
    serpMatch?.subcategoryValue ||
    (serpMatch?.category === 'city' && serpMatch?.categoryValue)
  ) {
    variables.location =
      serpMatch?.subcategoryValue || serpMatch?.categoryValue;
  }

  const { data, loading, error } = useQuery(SeoLinkBoxesDocument, {
    variables,
    errorPolicy: 'all',
    skip: !serpType,
  });

  const serialized = data?.seoLinkBoxes?.collection.map((item) => {
    if (item === null || item.entries === null) {
      return null;
    }

    return {
      kind: item.kind,
      entries: item.entries.map((entry) => ({
        id: entry?.name || '',
        link: { url: entry?.url || '', label: entry?.name || '' },
        count: entry?.jobCount || 0,
      })),
    };
  });

  return { data: serialized?.filter((item) => item !== null), loading, error };
};
